import { findAncestorWithComponent } from "../utils/scene-graph";

/**
 * Loops the given clip using this entity's animation mixer
 * @component loop-animation
 */
AFRAME.registerComponent("loop-animation", {
  schema: {
    paused: { type: "boolean", default: false },
    /* DEPRECATED: Use activeClipIndex instead since animation names are not unique */
    clip: { type: "string", default: "" },
    activeClipIndex: { type: "int", default: 0 },
    enableOnProximity: { type: "boolean", default: false },
    proximityRadius: { type: "number", default: 3 },
    startOffset: { type: "number", default: 0 },
    timeScale: { type: "number", default: 1 },
    activeClipIndices: { type: "array" }
  },

  init() {
    this.currentActions = [];
    this.stopAnimation = this.stopAnimation.bind(this);
    this.retryCount = 0;
    this.maxRetries = 20;
    this.retryInterval = 100;
    this.isInitialized = false;
    this.retryTimeout = null;
    this.onAnyComponentInitialized = this.onAnyComponentInitialized.bind(this);

    this.tryInitialize();
  },

  tryInitialize() {
    // Clear any existing timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    this.mixerEl = findAncestorWithComponent(this.el, "animation-mixer");

    if (!this.mixerEl) {
      // As a fallback, listen for future component initialization events on the scene
      if (!this._listeningForMixer) {
        this._listeningForMixer = true;
        this.el.sceneEl && this.el.sceneEl.addEventListener("componentinitialized", this.onAnyComponentInitialized);
        this.el.sceneEl && this.el.sceneEl.addEventListener("model-loaded", this.onAnyComponentInitialized);
      }

      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        this.retryTimeout = setTimeout(() => this.tryInitialize(), this.retryInterval);
      } else {
        console.warn("loop-animation component could not find an animation-mixer after retries.");
      }
      return;
    }

    this.retryCount = 0;
    this.isInitialized = true;

    if (this.data.enableOnProximity) {
      this.el.setAttribute("enable-on-proximity", { distance: this.data.proximityRadius });
      this.el.setAttribute("loop-animation", "paused", true);

      let tries = 0;
      const interval = setInterval(() => {
        this.mediaVideo = this.el.querySelector("[media-video]");
        this.mediaVideo?.setAttribute("visible", false);

        if (this.mediaVideo || tries++ > 3) {
          clearInterval(interval);
        }
      }, 1000);
    }

    // Start animation if not paused
    if (!this.data.paused) {
      this.updateClip();
    }
  },

  onAnyComponentInitialized(e) {
    const isMixer = e.detail && e.detail.name === "animation-mixer";
    const isModelLoaded = e.type === "model-loaded";
    const targetEl = e.target;
    if (!targetEl) return;

    const isAncestorOrSelf = (() => {
      const hasDomAPIs = !!targetEl.closest && !!this.el.closest && !!targetEl.contains;
      if (!hasDomAPIs) return true; // best effort if DOM methods differ
      return targetEl.contains(this.el);
    })();

    if ((isMixer || isModelLoaded) && isAncestorOrSelf && !this.isInitialized) {
      // Try immediately; remove retry delay to minimize latency
      this.tryInitialize();
    }
  },

  update(oldData) {
    // Only retry if not already initialized and not currently retrying
    if (!this.mixerEl && !this.retryTimeout && this.retryCount < this.maxRetries) {
      this.tryInitialize();
      return;
    }

    if (!this.mixerEl) return;

    if (oldData.clip !== this.data.clip || oldData.activeClipIndex !== this.data.activeClipIndex) {
      this.updateClip();
    }

    if (oldData.paused !== this.data.paused) {
      // Proximity initialization can defer action creation until playback starts.
      if (!this.data.paused && this.currentActions.length === 0) {
        this.updateClip();
      }
      for (let i = 0; i < this.currentActions.length; i++) {
        this.currentActions[i].paused = this.data.paused;
      }
    }
  },

  stopAnimation() {
    for (let i = 0; i < this.currentActions.length; i++) {
      this.currentActions[i].reset();
    }

    this.el.setAttribute("loop-animation", "paused", true);
  },

  updateClip() {
    if (!this.mixerEl) return;

    const { mixer, animations, mixerLinkedModel } = this.mixerEl.components["animation-mixer"];
    const { clip: clipName, activeClipIndex } = this.data;
    const { activeClipIndices } = this.data;

    if (animations.length === 0) {
      return;
    }

    let clips = [];
    if (activeClipIndices && activeClipIndices.length > 0) {
      clips = activeClipIndices.map(index => animations[index]);
    } else {
      if (clipName !== "") {
        const clipNames = clipName.split(",");
        for (let i = 0; i < clipNames.length; i++) {
          const n = clipNames[i];
          const a = animations.find(({ name }) => name === n);
          if (a) {
            clips.push(a);
          } else {
            console.warn(`Could not find animation named '${n}' in ${this.el.className}`);
          }
        }
      } else {
        clips = [animations[activeClipIndex]];
      }
    }

    if (!(clips && clips.length)) {
      console.warn("[loop-animation] No clips resolved to play", { data: this.data });
      return;
    }

    this.currentActions.length = 0;

    for (let i = 0; i < clips.length; i++) {
      const animationMixer = this.el.components["link-sequence"]?.data.linkedModel?.length ? mixerLinkedModel : mixer;
      const action = animationMixer.clipAction(clips[i], this.el.object3D);
      if (!action) {
        console.warn("[loop-animation] Failed to create action for clip", clips[i]?.name);
        continue;
      }

      this.mixerEl.components["animation-mixer"].setPausedComponent(true);

      action.enabled = true;
      action.time = this.data.startOffset;
      action.timeScale = this.data.timeScale;
      action.setLoop(THREE.LoopRepeat, Infinity).play();
      this.currentActions.push(action);
    }
  },

  destroy() {
    // Clean up timeout to prevent memory leaks
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    if (this._listeningForMixer) {
      this.el.sceneEl && this.el.sceneEl.removeEventListener("componentinitialized", this.onAnyComponentInitialized);
      this.el.sceneEl && this.el.sceneEl.removeEventListener("model-loaded", this.onAnyComponentInitialized);
      this._listeningForMixer = false;
    }

    for (let i = 0; i < this.currentActions.length; i++) {
      this.currentActions[i].enabled = false;
      this.currentActions[i].stop();
    }
    this.currentActions.length = 0;
  }
});
